import parse, { DomElement } from 'html-react-parser';
import { ReactNode } from 'react';

const URL_REGEX = /(\bhttps?:\/\/[^\s<>"']+)/i;
// Punctuation that ends a sentence rather than the link. A closing bracket stays when it closes
// one opened inside the link (`…/wiki/Foo_(bar)`).
const TRAILING_PUNCTUATION = '.,;:!?)]}';
const OPENING_BRACKETS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
// A path longer than this is cut in the middle, keeping its head and tail. The host never is.
const MAX_PATH_LENGTH = 40;
const PATH_HEAD_LENGTH = 15;
const PATH_TAIL_LENGTH = MAX_PATH_LENGTH - PATH_HEAD_LENGTH - 1; // 1 for the ellipsis
// Control characters, zero-width characters, and bidi marks, embeddings, overrides and isolates:
// decoding can put them in a label, and they can make it read differently from the link.
const INVISIBLE_CHARACTERS_REGEX =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001F\u007F-\u009F\u061C\u200B-\u200F\u2028-\u202E\u2060-\u2069\uFEFF]/g;

const countOf = (text: string, character: string) => text.split(character).length - 1;

// From the end, one character at a time (no regex backtracking on a long run of punctuation).
export const trimTrailingPunctuation = (url: string): string => {
  // Counts in the part of the link that is kept, computed once per bracket
  const counts: Record<string, number> = {};
  const countIn = (character: string) => {
    counts[character] = counts[character] ?? countOf(url, character);
    return counts[character];
  };
  let end = url.length;

  while (end > 0 && TRAILING_PUNCTUATION.includes(url[end - 1])) {
    const last = url[end - 1];
    const opening = OPENING_BRACKETS[last];

    // A closing bracket that closes one opened inside the link stays
    if (opening && countIn(opening) >= countIn(last)) {
      break;
    }

    if (opening) {
      counts[last] -= 1;
    }
    end -= 1;
  }

  return url.slice(0, end);
};

// A link as short text: its full host (punycode, as the browser reads it) and its decoded path,
// with no scheme or query string (a signed link's query is most of it). Only the path is cut.
export const shortLinkLabel = (url: string): string => {
  let parsed: URL;

  try {
    parsed = new URL(url);
  } catch {
    return url.replace(INVISIBLE_CHARACTERS_REGEX, '');
  }

  let path = parsed.pathname === '/' ? '' : parsed.pathname;

  try {
    path = decodeURIComponent(path);
  } catch {
    // Not valid percent-encoding: keep it as it is
  }

  path = path.replace(INVISIBLE_CHARACTERS_REGEX, '');

  if (path.length > MAX_PATH_LENGTH) {
    path = `${path.slice(0, PATH_HEAD_LENGTH)}…${path.slice(-PATH_TAIL_LENGTH)}`;
  }

  return `${parsed.host}${path}`;
};

// Plain text with every http(s) link turned into a short clickable link.
export const linkifyText = (text: string): ReactNode[] =>
  // With the capturing group, the links are the odd items
  text
    .split(URL_REGEX)
    .flatMap((part, index) => {
      if (index % 2 === 0) {
        return [part];
      }

      const url = trimTrailingPunctuation(part);

      return [
        <a key={`${index}-${url}`} href={url} target='_blank' rel='noopener noreferrer' title={url}>
          {shortLinkLabel(url)}
        </a>,
        part.slice(url.length),
      ];
    })
    .filter((part) => part !== '');

// Text already inside a link, or in the preview's hidden copy of it, stays as it is.
const isInsideLink = (node: DomElement | undefined): boolean => {
  const parent = node?.parent;

  if (!parent) {
    return false;
  }

  return (
    parent.name === 'a' ||
    !!parent.attribs?.class?.split(' ').includes('lp-link') ||
    isInsideLink(parent)
  );
};

// Message HTML as React elements, with the links in its text shown short and clickable.
export const parseMessageContent = (html: string) =>
  parse(html, {
    replace: (node) => {
      if (node.type !== 'text' || typeof node.data !== 'string' || isInsideLink(node)) {
        return undefined;
      }

      const parts = linkifyText(node.data);

      return parts.every((part) => typeof part === 'string') ? undefined : <>{parts}</>;
    },
  });
